export function withApplySessionLock<T>(key: string, work: () => Promise<T>): Promise<T> {
    return typeof navigator !== 'undefined' && navigator.locks ? navigator.locks.request(key, work) : work()
}

// Serialize initialization and publish only into the draft that started the request.
export function createApplyInitializer<T extends { data?: { uid?: string; step?: string | null } }>(options: {
    version: () => number
    read: () => { postInitUid?: string | null; opened?: boolean } | null
    request: () => Promise<T>
    publish: (result: T) => void
    lock: (work: () => Promise<T | null>) => Promise<T | null>
}) {
    let pending: { version: number; promise: Promise<T | null> } | null = null
    return (recover = false): Promise<T | null> => {
        const version = options.version()
        if (pending?.version === version) return pending.promise
        const initialUid = options.read()?.postInitUid
        const promise = options
            .lock(async () => {
                const draft = options.read()
                if (
                    version !== options.version() ||
                    !draft?.opened ||
                    (draft.postInitUid && (!recover || draft.postInitUid !== initialUid))
                )
                    return null
                const result = await options.request()
                const latest = options.read()
                if (version !== options.version() || !latest?.opened || latest.postInitUid !== draft.postInitUid)
                    return null
                options.publish(result)
                return result
            })
            .finally(() => {
                if (pending?.promise === promise) pending = null
            })
        pending = { version, promise }
        return promise
    }
}
